import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Use resolvedLanguage — always a clean 2-letter code ('en' or 'es')
  const isSpanish = i18n.resolvedLanguage === 'es';

  const toggleLang = () => {
    const next = isSpanish ? 'en' : 'es';
    i18n.changeLanguage(next);
    // Explicitly persist so it survives navigation & refresh
    localStorage.setItem('i18nextLng', next);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const navLinks = [
    { label: t('nav_home'), path: '/' },
    { label: t('nav_services'), path: '/services' },
    { label: t('nav_areas'), path: '/service-areas' },
    { label: t('nav_blog'), path: '/blog' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/bec85c6e-a88e-49c5-9451-4240e50efb42.png"
            alt="Quinolux Glass & Mirrors Logo"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap ${
                scrolled
                  ? isActive(link.path)
                    ? 'text-black border-b-2 border-black pb-0.5'
                    : 'text-gray-600 hover:text-black'
                  : isActive(link.path)
                  ? 'text-white border-b-2 border-white pb-0.5'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA + Lang */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'border-gray-300 text-gray-600 hover:border-black hover:text-black'
                : 'border-white/40 text-white/80 hover:border-white hover:text-white'
            }`}
          >
            {isSpanish ? 'EN' : 'ES'}
          </button>
          <a
            href="tel:+17026042093"
            className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'border-black text-black hover:bg-black hover:text-white'
                : 'border-white text-white hover:bg-white hover:text-black'
            }`}
          >
            <i className="ri-phone-line text-base"></i>
            (702) 604-2093
          </a>
          <Link
            to="/free-quote"
            className="bg-black text-white text-sm font-medium px-5 py-2 rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('getFreeQuote')}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
            scrolled ? 'text-black' : 'text-white'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-white transition-all duration-300 overflow-hidden ${
          menuOpen ? 'max-h-screen border-t border-gray-100' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium py-2 border-b border-gray-100 cursor-pointer ${
                isActive(link.path) ? 'text-black' : 'text-gray-600'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Language / Idioma</span>
            <button
              onClick={toggleLang}
              className="text-xs font-bold px-4 py-1.5 rounded-full border border-gray-300 text-gray-700 cursor-pointer"
            >
              {isSpanish ? 'Switch to English' : 'Cambiar a Español'}
            </button>
          </div>
          <a
            href="tel:+17026042093"
            className="flex items-center gap-2 text-sm font-medium text-black py-2 cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            (702) 604-2093
          </a>
          <Link
            to="/free-quote"
            className="bg-black text-white text-sm font-medium px-5 py-3 rounded-full text-center cursor-pointer"
          >
            {t('getFreeQuote')}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
